<template>
  <ModalView
    :model-value="modelValue" title="Edit printable license" size="lg" scrollable
    ok-title="Save printable license" :busy="saving || logoBusy" :disable-ok="validationErrors.length > 0"
    @update:model-value="$emit('update:modelValue', $event)" @ok="save"
  >
    <template v-if="modelValue">
      <p>This design is shared by every email notification for this municipality that attaches a printable license. Saving here updates the shared design immediately.</p>
      <PrintableLicenseSettings v-model="draft" :city="city" :disabled="saving" @busy="logoBusy = $event" />
      <div v-if="error" class="alert alert-danger mt-3" role="alert">{{ error }}</div>
      <ul v-if="validationErrors.length" class="text-danger mt-3">
        <li v-for="message in validationErrors" :key="message">{{ message }}</li>
      </ul>
      <button type="button" class="btn btn-outline-secondary mt-3" :disabled="saving || logoBusy || previewLoading || validationErrors.length > 0" @click="preview">
        {{ previewLoading ? 'Preparing preview…' : 'Preview PDF' }}
      </button>
      <div v-if="previewError" class="alert alert-danger mt-3" role="alert">{{ previewError }}</div>
      <EmailPdfAttachments class="mt-3" :attachments="attachments" />
    </template>
  </ModalView>
</template>

<script>
import ModalView from '@/components/common/ModalView.vue';
import PrintableLicenseSettings from './PrintableLicenseSettings.vue';
import EmailPdfAttachments from './EmailPdfAttachments.vue';
import { municipalityPrintableLicense, validatePrintableLicense } from '@strenforcement/domain/printableLicense';

export default {
  components: { ModalView, PrintableLicenseSettings, EmailPdfAttachments },
  props: { modelValue: Boolean, city: { type: Object, required: true } },
  emits: ['update:modelValue', 'saved'],
  data() {
    return { draft: {}, saving: false, logoBusy: false, error: null, attachments: [], previewError: null, previewLoading: false, previewToken: 0, session: 0 };
  },
  computed: {
    validationErrors() { return validatePrintableLicense(this.draft, { cityId: this.city.id }); }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(open) {
        this.session++;
        this.invalidatePreview();
        if (open) {
          this.draft = { ...(this.city.printableLicense || municipalityPrintableLicense(this.city.data)) };
          this.error = null;
        }
      }
    },
    draft: { deep: true, handler() { this.invalidatePreview(); } }
  },
  beforeUnmount() { this.session++; this.invalidatePreview(); },
  methods: {
    invalidatePreview() { this.previewToken++; this.attachments = []; this.previewError = null; this.previewLoading = false; },
    async preview() {
      if (this.saving || this.logoBusy || this.validationErrors.length) return;
      const token = ++this.previewToken;
      this.previewLoading = true;
      this.previewError = null;
      try {
        const result = await this.city.previewPrintableLicense({ ...this.draft });
        if (token === this.previewToken) this.attachments = result.attachments;
      } catch (error) {
        if (token === this.previewToken) this.previewError = error.message || String(error);
      } finally {
        if (token === this.previewToken) this.previewLoading = false;
      }
    },
    async save() {
      if (this.saving || this.logoBusy || this.validationErrors.length) return;
      const session = this.session;
      this.saving = true;
      this.error = null;
      try {
        await this.city.updatePrintableLicense({ ...this.draft });
        if (session === this.session) {
          this.$emit('saved');
          this.$emit('update:modelValue', false);
        }
      } catch (error) {
        if (session === this.session) this.error = error.message || String(error);
      } finally { this.saving = false; }
    }
  }
};
</script>
